import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, LinearProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type ChangeEvent, type DragEvent, type FC, useMemo, useRef, useState } from "react";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { ThemedSvg } from "../../components/UIToolkit/ThemedSvg";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useSyncShifts } from "../../hooks/useSyncShifts";
import { INK_BLACK, NEUTRAL, SURFACE } from "../../theme/tokens";
import type { IgnoredExternalShift } from "../../utils/models";
import { MissingNursesTable } from "./MissingNursesTable/MissingNursesTable";

type FileUploadModalProps = {
	onClose: () => void;
	startDate?: DateTime;
	endDate?: DateTime;
	source?: string;
};

export const FileUploadModal: FC<FileUploadModalProps> = ({ onClose, startDate, endDate }) => {
	const { chosenDepartment, refreshDepartmentData } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { mutateAsync: syncShifts, isPending } = useSyncShifts();
	const [selectedFile, setSelectedFile] = useState<File>();
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [isReady, setIsReady] = useState<boolean>(false);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [ignoredShifts, setIgnoredShifts] = useState<IgnoredExternalShift[]>();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const startProgressAnimation = async (): Promise<void> => {
		setIsReady(false);
		setUploadProgress(0);

		await new Promise<void>((resolve) => {
			const interval = setInterval(() => {
				setUploadProgress((prev) => {
					const newProgress = Math.min(prev + 2, 100);
					if (newProgress === 100) {
						clearInterval(interval);
						setTimeout(() => setIsReady(true), 500);
						resolve();
					}
					return newProgress;
				});
			}, 50);
		});
	};

	const handleDragOver = (e: DragEvent): void => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent): void => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
		const file = e.target.files?.[0];
		if (file) {
			if (file.name.endsWith(".xls") || file.name.endsWith(".xlsx") || file.name.endsWith(".csv")) {
				setSelectedFile(file);
				await startProgressAnimation();
			} else {
				showSnackbar?.("Please upload an Excel or CSV file", "error");
			}
		}
	};

	const handleDrop = async (e: DragEvent): Promise<void> => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];

		if (file) {
			if (file.name.endsWith(".xls") || file.name.endsWith(".xlsx") || file.name.endsWith(".csv")) {
				setSelectedFile(file);
				await startProgressAnimation();
			} else {
				showSnackbar?.("Please upload an Excel or CSV file", "error");
			}
		}
	};

	const handleImport = async (): Promise<void> => {
		if (!selectedFile || !isReady || uploadProgress < 100 || isPending) return;
		await syncShifts(
			{
				file: selectedFile,
				startDate,
				endDate,
			},
			{
				onSuccess: (data) => {
					if (!data) return;
					if (data.toIgnore.length) setIgnoredShifts(data.toIgnore);
					else onClose();
				},
				onError: () => {
					setUploadProgress(0);
					setIsReady(false);
				},
			},
		);
		await refreshDepartmentData(chosenDepartment.department_id, false);
	};

	const ignoredShiftsCount = useMemo<number>(() => ignoredShifts?.length || 0, [ignoredShifts]);

	return (
		<Box sx={{ width: "800px", bgcolor: SURFACE, borderRadius: "12px", p: 4, boxShadow: `0px 6px 16px ${alpha(INK_BLACK, 0.15)}` }}>
			<Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
				Import table from file
			</Typography>

			<Box
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				sx={(theme) => ({
					border: `3px dashed ${theme.palette.primary.main}`,
					borderRadius: "12px",
					p: 6,
					textAlign: "center",
					cursor: "pointer",
					mb: 3,
					transition: "background-color 0.2s",
					backgroundColor: isDragging ? NEUTRAL[100] : "transparent",
					"&:hover": {
						backgroundColor: NEUTRAL[100],
					},
				})}
				onClick={() => fileInputRef.current?.click()}
			>
				<ThemedSvg src="/upload.svg" alt="Upload" size={64} color="var(--inhouse-primary)" />
				<Typography variant="h6">Drag & Drop or Choose file to Upload</Typography>
				<Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
					Supported formats CSV, XLS, XLSX
				</Typography>
				<input type="file" hidden ref={fileInputRef} accept=".csv,.xls,.xlsx" onChange={handleFileSelect} />
			</Box>

			{selectedFile && (
				<Box sx={{ mb: 3 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
							<img src={"/excel.svg"} alt="Excel file" style={{ width: 32, height: 32 }} />
							<Typography variant="h6">{selectedFile.name}</Typography>
							<Typography variant="body1" color="textSecondary">
								{selectedFile.size < 1024
									? `${selectedFile.size} B`
									: selectedFile.size < 1024 * 1024
										? `${(selectedFile.size / 1024).toFixed(1)} KB`
										: `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}
							</Typography>
						</Box>
						<IconButton size="medium" onClick={() => setSelectedFile(undefined)}>
							<CloseIcon sx={{ fontSize: 24 }} />
						</IconButton>
					</Box>
					<LinearProgress
						variant="determinate"
						value={uploadProgress}
						sx={(theme) => ({
							height: 8,
							borderRadius: 4,
							bgcolor: alpha(theme.palette.primary.main, 0.2),
							"& .MuiLinearProgress-bar": {
								bgcolor: theme.palette.primary.main,
							},
						})}
					/>
					{!!uploadProgress && (
						<Typography variant="body1" color="textSecondary" align="center" sx={{ mt: 2 }}>
							{uploadProgress}%
						</Typography>
					)}
				</Box>
			)}

			{ignoredShifts ? (
				<Box>
					<Box sx={{ py: 2 }}>
						<MissingNursesTable ignoredShifts={ignoredShifts} />
					</Box>
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
						<SystemButton onClick={onClose}>Continue (skip {ignoredShiftsCount} errors)</SystemButton>
					</Box>
				</Box>
			) : (
				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
					<Button
						variant="outlined"
						onClick={onClose}
						size="large"
						sx={(theme) => ({
							color: theme.palette.primary.main,
							borderColor: theme.palette.primary.main,
							"&:hover": {
								borderColor: theme.palette.primary.dark,
							},
							px: 4,
						})}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={!selectedFile || !isReady || uploadProgress < 100 || isPending}
						onClick={handleImport}
						size="large"
						sx={(theme) => ({
							bgcolor: theme.palette.primary.main,
							"&:hover": {
								bgcolor: theme.palette.primary.dark,
							},
							px: 4,
						})}
					>
						{isPending ? "Importing..." : "Import"}
					</Button>
				</Box>
			)}
		</Box>
	);
};
